import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';

export const useTrialStatus = () => {
  const { user } = useAuth();
  const [isActive, setIsActive] = useState(null);
  const [trialEndDate, setTrialEndDate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [subscriptionEndDate, setSubscriptionEndDate] = useState(null);

  useEffect(() => {
    const fetchStatus = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data();
        const subscription = userData?.subscription;

        if (!subscription) {
          setIsActive(false);
          setIsLoading(false);
          return;
        }

        // Check paid subscription status first
        if (subscription.isActive) {
          setSubscriptionStatus('active');
          setSubscriptionEndDate(
            typeof subscription.currentPeriodEnd === 'string'
              ? new Date(subscription.currentPeriodEnd)
              : subscription.currentPeriodEnd.toDate()
          );
          setIsActive(true);
          setShowExpiredModal(false);
        } 
        // Then check trial status
        else if (subscription.isTrialActive) {
          const trialEnd = typeof subscription.trialEndDate === 'string'
            ? new Date(subscription.trialEndDate)
            : subscription.trialEndDate.toDate();

          setTrialEndDate(trialEnd);
          const isTrialStillActive = trialEnd > new Date();
          setIsActive(isTrialStillActive);

          // Show expiration modal if trial has ended and user hasn't subscribed
          if (!isTrialStillActive && !subscription.isActive) {
            setShowExpiredModal(true);
          }
        } else {
          setIsActive(false);
          if (!subscription.isActive) {
            setShowExpiredModal(true);
          }
        }
      } catch (error) {
        console.error('Error fetching status:', error);
        setIsActive(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStatus();
  }, [user]);

  const formatRemainingTime = () => {
    if (subscriptionStatus === 'active' && subscriptionEndDate) {
      const now = new Date();
      const diffTime = Math.abs(subscriptionEndDate - now);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return `Renews in ${diffDays} days`;
    }

    if (!trialEndDate) return 'No trial data';
    
    const now = new Date();
    if (now > trialEndDate) return 'Trial has ended';

    const diffTime = Math.abs(trialEndDate - now);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return `${diffDays} days remaining`;
  };

  const getStatusBadge = () => {
    if (subscriptionStatus === 'active') {
      return 'PRO PLAN';
    }
    return isActive ? 'PRO TRIAL' : 'TRIAL ENDED';
  };

  const handleContinue = async () => {
    try {
      setShowExpiredModal(false);
    } catch (error) {
      console.error('Error in handleContinue:', error);
    }
  };

  return {
    isActive,
    formatRemainingTime,
    trialEndDate,
    isLoading,
    showExpiredModal,
    handleContinue,
    subscriptionStatus,
    getStatusBadge
  };
};